import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { shopifyApi } from '../api/shopifyClient';

/**
 * App Bridge Context
 * Provides access to the Shopify App Bridge instance loaded via CDN
 * All child components can use the useAppBridge() hook to access App Bridge
 */

// Minimal type definition for Shopify App Bridge global
// Full types are provided by @shopify/app-bridge-types package
// Note: The new App Bridge (App Home) does NOT have a redirect API.
// Use window.open(url, '_top') to break out of the iframe instead.
interface ShopifyGlobalType {
  idToken: () => Promise<string>;
  toast: {
    show: (options: { message: string; duration?: number; isError?: boolean }) => void;
  };
  // Add other methods as needed
}

interface ShopifyAppBridgeContextValue {
  shopify: ShopifyGlobalType | null;
  isReady: boolean;
  appBridgeError: string | null;
}

const ShopifyAppBridgeContext = createContext<ShopifyAppBridgeContextValue>({
  shopify: null,
  isReady: false,
  appBridgeError: null,
});

interface ShopifyAppBridgeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes App Bridge available to all child components
 * Waits for the CDN script to load and initializes the global shopify object
 */
const APP_BRIDGE_CDN_URL = 'https://cdn.shopify.com/shopifycloud/app-bridge.js';

/** How long to wait for window.shopify to appear before surfacing an error (ms) */
const APP_BRIDGE_TIMEOUT_MS = 10_000;

function getShopFromUrl(): string | null {
  const rawShop = new URLSearchParams(window.location.search).get('shop');
  if (!rawShop) return null;

  const normalized = rawShop.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function ensureApiKeyMetaTag(apiKey: string): void {
  let metaTag = document.querySelector<HTMLMetaElement>('meta[name="shopify-api-key"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = 'shopify-api-key';
    document.head.appendChild(metaTag);
  }

  metaTag.content = apiKey;
}

async function resolveRuntimeApiKey(shop: string | null): Promise<string | null> {
  try {
    const config = await shopifyApi.getAppBridgeConfig(shop ?? undefined);
    return config.apiKey || null;
  } catch {
    return null;
  }
}

async function ensureAppBridgeScript(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${APP_BRIDGE_CDN_URL}"]`);

  if (existing) {
    // If a matching script tag already exists, do not await a new load event.
    // In SPA navigation, the script may have already fired 'load', and adding
    // listeners afterward would never resolve. Bootstrap readiness is enforced
    // by waitForAppBridgeGlobal() polling.
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APP_BRIDGE_CDN_URL;
    script.async = false;
    script.defer = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Shopify App Bridge script'));

    const firstScript = document.head.querySelector('script');
    if (firstScript) {
      document.head.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

async function waitForAppBridgeGlobal(): Promise<void> {
  const start = Date.now();

  await new Promise<void>((resolve, reject) => {
    const check = () => {
      if (window.shopify?.idToken) {
        resolve();
        return;
      }

      if (Date.now() - start >= APP_BRIDGE_TIMEOUT_MS) {
        reject(new Error(
          'Shopify App Bridge did not load within the expected time. ' +
          'Please ensure this page is opened inside the Shopify admin.'
        ));
        return;
      }

      setTimeout(check, 100);
    };

    check();
  });
}

export function ShopifyAppBridgeProvider({ children }: ShopifyAppBridgeProviderProps) {
  const [shopify, setShopify] = useState<ShopifyGlobalType | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [appBridgeError, setAppBridgeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const shop = getShopFromUrl();
        const apiKey = await resolveRuntimeApiKey(shop);

        if (!apiKey) {
          throw new Error('Shopify App Bridge API key is not configured.');
        }

        ensureApiKeyMetaTag(apiKey);
        await ensureAppBridgeScript();
        await waitForAppBridgeGlobal();

        if (cancelled) return;

        setShopify(window.shopify as ShopifyGlobalType);
        setIsReady(true);
        setAppBridgeError(null);
      } catch (error) {
        if (cancelled) return;
        setIsReady(false);
        setAppBridgeError(error instanceof Error ? error.message : 'Shopify App Bridge failed to initialize');
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ shopify, isReady, appBridgeError }), [shopify, isReady, appBridgeError]);

  return (
    <ShopifyAppBridgeContext.Provider value={value}>
      {children}
    </ShopifyAppBridgeContext.Provider>
  );
}

/**
 * Hook to access App Bridge in functional components
 * Returns the shopify global object and ready state
 */
export function useAppBridge() {
  const context = useContext(ShopifyAppBridgeContext);
  if (!context) {
    throw new Error('useAppBridge must be used within ShopifyAppBridgeProvider');
  }
  return context;
}
